import Briefcase from '@lideralia/alife-uikit/dist/atoms/imgs/Briefcase';
import Camera from '@lideralia/alife-uikit/dist/atoms/imgs/Camera';
import Diamond from '@lideralia/alife-uikit/dist/atoms/imgs/Diamond';
import Happy from '@lideralia/alife-uikit/dist/atoms/imgs/Happy';
import Heart from '@lideralia/alife-uikit/dist/atoms/imgs/Heart';
import Music from '@lideralia/alife-uikit/dist/atoms/imgs/Music';
import Plant from '@lideralia/alife-uikit/dist/atoms/imgs/Plant';
import Sunrise from '@lideralia/alife-uikit/dist/atoms/imgs/Sunrise';
import { Container } from '@lideralia/alife-uikit/dist/atoms/Layout';
import { Text } from '@lideralia/alife-uikit/dist/atoms/Typography';
import { QuestionCard } from '@lideralia/alife-uikit/dist/molecules/BiographicalEditor/QuestionCard';
import { BlockCard } from '@lideralia/alife-uikit/dist/molecules/BlockCard';
import { EditorBlockFooter } from '@lideralia/alife-uikit/dist/organisms/BiographicalEditor/EditorBlockFooter';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { getActualLanguage } from '../../../../common/functions/lang';
import { saveBlock } from '../../actions';
import { BLOCKS_CONFIG, REQUIRED_BLOCKS } from '../../config';

const ICON_MAP = {
  plant: <Plant />,
  camera: <Camera />,
  heart: <Heart />,
  sunrise: <Sunrise />,
  happy: <Happy />,
  briefcase: <Briefcase />,
  diamond: <Diamond />,
  music: <Music />,
};


const FREE_QUESTION_KEY = 'free';

export const BlockQuestionScreen = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const intl = useIntl();
  const { idLine, idName, blockKey } = useParams();
  const lang = getActualLanguage();

  const { memory, status } = useSelector((state) => state.biographicalEditor);
  const [answers, setAnswers] = useState({ [FREE_QUESTION_KEY]: '' });

  const blockConfig = BLOCKS_CONFIG.find((b) => b.key === blockKey);
  const blockIndex = REQUIRED_BLOCKS.findIndex((b) => b.key === blockKey);
  const blockNumber = blockIndex + 1;

  // Pre-fill if block already has an answer
  useEffect(() => {
    if (memory?.blocks) {
      const existingBlock = memory.blocks.find((b) => b.block_key === blockKey);
      if (existingBlock?.answers?.length > 0) {
        const freeAnswer = existingBlock.answers.find((a) => a.question_key === FREE_QUESTION_KEY);
        if (freeAnswer) {
          setAnswers({ [FREE_QUESTION_KEY]: freeAnswer.answer ?? '' });
        }
      }
    }
  }, [memory, blockKey]);

  const isSaving = status === 'saving';
  const canSave = (answers[FREE_QUESTION_KEY] || '').trim().length > 0 && !isSaving;

  const handleAnswerChange = (questionKey, value) => {
    setAnswers((prev) => ({ ...prev, [questionKey]: value }));
  };

  const handleSave = () => {
    if (!canSave || !memory?.uuid) return;
    dispatch(saveBlock(idLine, blockKey, { [FREE_QUESTION_KEY]: answers[FREE_QUESTION_KEY].trim() }, memory.uuid));
  };

  // On save success, navigate back to block index
  const saveDone = useSelector((state) => state.biographicalEditor.saveDone);
  useEffect(() => {
    if (saveDone) {
      history.push(`/${lang}/lines/${idLine}/${idName}/biographical-editor/blocks`);
    }
  }, [saveDone]);

  const handleBack = () => {
    history.push(`/${lang}/lines/${idLine}/${idName}/biographical-editor/blocks`);
  };

  if (!blockConfig) {
    return null;
  }

  const questions = [
    {
      questionKey: FREE_QUESTION_KEY,
      question: intl.formatMessage({ id: `biographicalEditor.blocks.${blockKey}.question` }),
      hint: intl.formatMessage({ id: `biographicalEditor.blocks.${blockKey}.hint` }),
      placeholder: intl.formatMessage({ id: `biographicalEditor.blocks.${blockKey}.placeholder` }),
    },
  ];

  return (
    <Container paddingY={3} paddingBottom={8} maxWidth={['400px']}>
      {/* Header with back button */}
      <Text
        as="h1"
        sizeText="display24"
        fontWeight="bold"
        color="black84"
        style={{ marginBottom: 18 }}
        >
        <FormattedMessage id="biographicalEditor.biographicalMemory" />
      </Text>

      {/* Block card header */}
      <BlockCard
          blockKey={blockConfig.key}
          name={intl.formatMessage({ id: `biographicalEditor.blocks.${blockKey}.name` })}
          icon={ICON_MAP[blockConfig.icon]}
          iconColor={blockConfig.iconColor}
          style={{ marginBottom: 20 }}
      />

      {/* Question card */}
      <div style={{ marginBottom: 24 }}>
        <QuestionCard
          questions={questions}
          answers={answers}
          onChange={handleAnswerChange}
        />
      </div>

      {/* Footer actions */}
      <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 100 }}>
        <EditorBlockFooter
            currentStep={blockNumber}
            totalSteps={REQUIRED_BLOCKS.length}
            stepLabel={intl.formatMessage(
              { id: 'biographicalEditor.blockQuestion.step' },
              { current: blockNumber, total: REQUIRED_BLOCKS.length }
            )}
            blockName={intl.formatMessage({ id: `biographicalEditor.blocks.${blockKey}.name` })}
            backLabel={intl.formatMessage({ id: 'biographicalEditor.blockQuestion.back' })}
            saveLabel={intl.formatMessage({ id: 'biographicalEditor.blockQuestion.save' })}
            saveDisabled={!canSave}
            onBack={handleBack}
            onSave={handleSave}
        />
      </div>
    </Container>
  );
};

export default BlockQuestionScreen;
